import { Fragment, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  DocumentDuplicateIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  Cog6ToothIcon,
  CalendarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemNotifications } from '../../hooks/useSystemNotifications';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

export const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount, notifications } = useSystemNotifications();
  const { t } = useTranslation();

  // Role-based navigation
  const isManager = user?.roles?.some((role: any) => role.name?.toLowerCase() === 'manager');
  const isAdmin = user?.roles?.some((role: any) => role.name?.toLowerCase() === 'admin');
  const isHR = user?.roles?.some((role: any) => role.name?.toLowerCase() === 'hr');
  
  const navigationSections = [
    {
      name: 'Główne',
      items: [
        { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon },
      ]
    },
    {
      name: 'Urlopy',
      items: [
        { name: t('navigation.leaves'), href: '/leaves', icon: DocumentDuplicateIcon },
        { name: t('navigation.substitutions'), href: '/substitutions', icon: UsersIcon },
        ...(isManager || isAdmin || isHR ? [
          { name: t('navigation.teamLeaves'), href: '/leaves/team', icon: UsersIcon },
          { name: t('navigation.team'), href: '/team', icon: UsersIcon },
          { name: t('navigation.calendar'), href: '/team-calendar', icon: CalendarIcon },
          { name: t('navigation.reports'), href: '/reports', icon: ChartBarIcon },
        ] : [])
      ]
    },
    {
      name: 'Księgowość',
      items: [
        { name: 'Faktury', href: '/accounting/invoices', icon: DocumentTextIcon },
        { name: 'Płatności', href: '/accounting/payments', icon: CreditCardIcon },
        { name: 'Zaległości', href: '/accounting/overdue', icon: ExclamationTriangleIcon },
        { name: 'Raporty finansowe', href: '/accounting/reports', icon: CurrencyDollarIcon },
        ...(isAdmin ? [
          { name: 'Konfiguracja przypomnień', href: '/accounting/reminder-config', icon: EnvelopeIcon },
        ] : [])
      ]
    },
    {
      name: 'Administracja',
      items: [
        ...(isAdmin || isHR ? [
          { name: t('navigation.notifications'), href: '/notifications', icon: BellIcon },
          { name: t('navigation.users'), href: '/users', icon: Cog6ToothIcon },
          { name: t('navigation.leaveTypes'), href: '/leave-types', icon: DocumentDuplicateIcon },
        ] : []),
        ...(isAdmin ? [
          { name: t('navigation.departments'), href: '/departments', icon: BuildingOfficeIcon },
          { name: t('navigation.roles'), href: '/roles', icon: ShieldCheckIcon },
        ] : [])
      ]
    }
  ].filter(section => section.items.length > 0);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">{t('navigation.closeSidebar')}</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center">
                    <img 
                      src="/nodecrafts_leaveapp.svg" 
                      alt="NodeCrafts LeaveApp" 
                      className="h-12 w-auto"
                    />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      {navigationSections.map((section) => (
                        <li key={section.name}>
                          <div className="text-xs font-semibold leading-6 text-gray-400 uppercase">
                            {section.name}
                          </div>
                          <ul role="list" className="-mx-2 mt-2 space-y-1">
                            {section.items.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  className={clsx(
                                    location.pathname === item.href
                                      ? 'bg-gray-50 text-primary-600'
                                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon
                                    className={clsx(
                                      location.pathname === item.href
                                        ? 'text-primary-600'
                                        : 'text-gray-400 group-hover:text-primary-600',
                                      'h-6 w-6 shrink-0'
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                      <li className="mt-auto">
                        <button
                          onClick={handleLogout}
                          className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-primary-600 w-full"
                        >
                          <ArrowRightOnRectangleIcon
                            className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-600"
                            aria-hidden="true"
                          />
                          {t('auth.logout', 'Wyloguj się')}
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <img 
              src="/nodecrafts_leaveapp.svg" 
              alt="NodeCrafts LeaveApp" 
              className="h-12 w-auto"
            />
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              {navigationSections.map((section) => (
                <li key={section.name}>
                  <div className="text-xs font-semibold leading-6 text-gray-400 uppercase">
                    {section.name}
                  </div>
                  <ul role="list" className="-mx-2 mt-2 space-y-1">
                    {section.items.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={clsx(
                            location.pathname === item.href
                              ? 'bg-gray-50 text-primary-600'
                              : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                          )}
                        >
                          <item.icon
                            className={clsx(
                              location.pathname === item.href
                                ? 'text-primary-600'
                                : 'text-gray-400 group-hover:text-primary-600',
                              'h-6 w-6 shrink-0'
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
              <li className="mt-auto">
                <button
                  onClick={handleLogout}
                  className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-primary-600 w-full"
                >
                  <ArrowRightOnRectangleIcon
                    className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-primary-600"
                    aria-hidden="true"
                  />
                  {t('auth.logout', 'Wyloguj się')}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">{t('navigation.openSidebar')}</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <LanguageSwitcher />
              
              <Link to="/notifications" className="relative -m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                <span className="sr-only">{t('navigation.viewNotifications')}</span>
                <BellIcon className="h-6 w-6" aria-hidden="true" />
                {unreadCount && unreadCount > 0 && (
                  <span className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              <Link to="/settings" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                <span className="sr-only">{t('navigation.settings', 'Settings')}</span>
                <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
              </Link>

              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" aria-hidden="true" />

              <div className="flex items-center gap-x-4">
                <div className="text-sm text-right">
                  <p className="font-medium text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-gray-500">{user?.email}</p>
                </div>
                <Link to="/settings">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-500">
                    <span className="text-sm font-medium leading-none text-white">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};